import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  ListTodo,
  ArrowLeft,
  Clock,
  CheckCircle2,
  AlertCircle,
  User,
  Calendar,
  Phone,
  ChevronRight,
  Loader2,
  Search,
  Filter,
  Edit2,
  Send,
  FileText
} from "lucide-react";
import { authGet, authPut, isAdvisor, getUserRole } from "../utils/auth";

// Task types
const TASK_TYPES = [
  { value: "pre_underwriting", label: "Pre-Underwriting", color: "amber" },
  { value: "prepare_quote", label: "Prepare Quote", color: "blue" },
  { value: "document_collection", label: "Document Collection", color: "green" },
  { value: "follow_up", label: "Follow Up", color: "purple" }
];

// Status options
const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "amber" },
  { value: "in_progress", label: "In Progress", color: "blue" },
  { value: "completed", label: "Completed", color: "green" }
];

// Priority options
const PRIORITY_OPTIONS = [
  { value: "high", label: "High", color: "red" },
  { value: "medium", label: "Medium", color: "amber" },
  { value: "low", label: "Low", color: "slate" }
];

export default function EmployeeTasks() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [responseForm, setResponseForm] = useState({ task_status: "", response_notes: "" });
  
  const userRole = getUserRole();

  useEffect(() => {
    fetchTasks();
  }, [statusFilter]);

  const fetchTasks = async () => {
    try {
      let url = '/api/advisor/employee/tasks';
      if (statusFilter && statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const res = await authGet(url);
      if (res.ok) {
        setTasks(res.data.tasks || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTask = async () => {
    if (!selectedTask) return;
    
    setSaving(true);
    try {
      const res = await authPut(
        `/api/advisor/leads/${selectedTask.lead_id}/activities/${selectedTask.id}`,
        responseForm
      );
      
      if (res.ok) {
        toast.success("Task updated!");
        setShowUpdateModal(false);
        setSelectedTask(null);
        setResponseForm({ task_status: "", response_notes: "" });
        fetchTasks();
      } else {
        throw new Error(res.data?.detail || "Failed to update task");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const openUpdateModal = (task) => {
    setSelectedTask(task);
    setResponseForm({
      task_status: task.task_status || "pending",
      response_notes: task.response_notes || ""
    });
    setShowUpdateModal(true);
  };

  const getTaskTypeInfo = (type) => {
    return TASK_TYPES.find(t => t.value === type) || TASK_TYPES[0];
  };

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  const getPriorityInfo = (priority) => {
    return PRIORITY_OPTIONS.find(p => p.value === priority) || PRIORITY_OPTIONS[1];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTasks = tasks.filter(task => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      task.task_title?.toLowerCase().includes(query) ||
      task.lead_name?.toLowerCase().includes(query) ||
      task.task_description?.toLowerCase().includes(query)
    );
  });

  const pendingCount = tasks.filter(t => t.task_status === 'pending').length;
  const inProgressCount = tasks.filter(t => t.task_status === 'in_progress').length;
  const completedCount = tasks.filter(t => t.task_status === 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#A87608]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#C9D4E6] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/advisor/dashboard")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <ListTodo className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-[#1B2A4A]">Tasks</h1>
                  <p className="text-[#3A4557] text-xs">
                    {userRole === 'employee' ? 'Your assigned tasks' : 'All tasks across leads'}
                  </p>
                </div>
              </div>
            </div>
            
            {isAdvisor() && (
              <Button
                onClick={() => navigate("/advisor/leads")}
                className="bg-blue-500 hover:bg-[#EDF1F8] text-white"
              >
                View Leads
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card 
            className={`bg-[#D4A017]/10 border-[#D4A017]/30 cursor-pointer transition-all ${statusFilter === 'pending' ? 'ring-2 ring-[#D4A017]' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            <CardContent className="p-4 text-center">
              <p className="text-[#A87608] text-2xl font-bold">{pendingCount}</p>
              <p className="text-[#3A4557] text-sm">Pending</p>
            </CardContent>
          </Card>
          <Card 
            className={`bg-[#EDF1F8]/30 border-blue-500/30 cursor-pointer transition-all ${statusFilter === 'in_progress' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setStatusFilter('in_progress')}
          >
            <CardContent className="p-4 text-center">
              <p className="text-[#2E3B57] text-2xl font-bold">{inProgressCount}</p>
              <p className="text-[#3A4557] text-sm">In Progress</p>
            </CardContent>
          </Card>
          <Card 
            className={`bg-green-500/10 border-green-500/30 cursor-pointer transition-all ${statusFilter === 'completed' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setStatusFilter('completed')}
          >
            <CardContent className="p-4 text-center">
              <p className="text-green-600 text-2xl font-bold">{completedCount}</p>
              <p className="text-[#3A4557] text-sm">Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/50 border-[#C9D4E6] text-[#1B2A4A]"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] bg-white/50 border-[#C9D4E6] text-[#1B2A4A]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#C9D4E6]">
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tasks List */}
        <div className="space-y-4">
          {filteredTasks.length === 0 ? (
            <Card className="bg-white/50 border-[#C9D4E6]">
              <CardContent className="p-8 text-center">
                <ListTodo className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                <p className="text-[#3A4557]">No tasks found</p>
                <p className="text-[#3A4557] text-sm mt-1">
                  {statusFilter === 'pending' ? 'No pending tasks right now!' : 'Try changing the filter'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredTasks.map((task) => {
              const typeInfo = getTaskTypeInfo(task.task_type);
              const statusInfo = getStatusInfo(task.task_status);
              const priorityInfo = getPriorityInfo(task.priority);

              return (
                <Card 
                  key={task.id} 
                  className={`bg-white/50 border-[#C9D4E6] hover:bg-[#EDF1F8]/50 transition-colors ${
                    task.task_status === 'pending' && task.priority === 'high' ? 'border-l-4 border-l-red-500' : ''
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Task Type Icon */}
                      <div className={`h-12 w-12 rounded-xl bg-${typeInfo.color}-500/20 flex items-center justify-center shrink-0`}>
                        <FileText className={`h-6 w-6 text-${typeInfo.color}-700`} />
                      </div>

                      {/* Task Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <h3 className="text-[#1B2A4A] font-semibold">
                              {task.task_title || typeInfo.label}
                            </h3>
                            <p className="text-[#3A4557] text-sm flex items-center gap-2">
                              <User className="h-3 w-3" />
                              {task.lead_name || 'Unknown Lead'}
                              {task.lead_phone && (
                                <>
                                  <Phone className="h-3 w-3 ml-2" />
                                  {task.lead_phone}
                                </>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Priority Badge */}
                            <span className={`text-xs px-2 py-1 rounded-full bg-${priorityInfo.color}-500/20 text-${priorityInfo.color}-700`}>
                              {priorityInfo.label}
                            </span>
                            {/* Status Badge */}
                            <span className={`text-xs px-2 py-1 rounded-full bg-${statusInfo.color}-500/20 text-${statusInfo.color}-700`}>
                              {statusInfo.label}
                            </span>
                          </div>
                        </div>

                        {task.task_description && (
                          <p className="text-[#3A4557] text-sm mb-2 line-clamp-2">{task.task_description}</p>
                        )}

                        {task.companies && task.companies.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {task.companies.map((company, idx) => (
                              <span key={idx} className="bg-[#EDF1F8]/40 text-[#2E3B57] text-xs px-2 py-0.5 rounded">
                                {company}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#C9D4E6]">
                          <span className="text-[#3A4557] text-xs flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(task.created_at)}
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openUpdateModal(task)}
                              className="border-[#D4A017]/50 text-[#A87608] hover:bg-[#D4A017]/10 h-8"
                              data-testid={`update-task-${task.id}`}
                            >
                              <Edit2 className="h-3 w-3 mr-1" />
                              Update Status
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/advisor/leads/${task.lead_id}`)}
                              className="text-[#2E3B57] hover:text-[#2E3B57] h-8"
                            >
                              View Lead <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </main>

      {/* Update Task Modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Update Task Status
            </DialogTitle>
          </DialogHeader>

          {selectedTask && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-[#EDF1F8]/50 rounded-lg">
                <p className="text-[#1B2A4A] font-medium">
                  {selectedTask.task_title || getTaskTypeInfo(selectedTask.task_type)?.label}
                </p>
                <p className="text-[#3A4557] text-sm mt-1">Lead: {selectedTask.lead_name}</p>
                {selectedTask.task_description && (
                  <p className="text-[#3A4557] text-sm mt-2">{selectedTask.task_description}</p>
                )}
              </div>
              
              <div>
                <Label className="text-[#3A4557] text-xs">Status</Label>
                <Select 
                  value={responseForm.task_status} 
                  onValueChange={(v) => setResponseForm({ ...responseForm, task_status: v })}
                >
                  <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-[#3A4557] text-xs">Response Notes</Label>
                <textarea
                  value={responseForm.response_notes}
                  onChange={(e) => setResponseForm({ ...responseForm, response_notes: e.target.value })}
                  placeholder="Add your response or completion notes..."
                  rows={3}
                  className="mt-1 w-full px-3 py-2 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUpdateModal(false);
                    setSelectedTask(null);
                  }}
                  className="flex-1 border-[#C9D4E6]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateTask}
                  disabled={saving}
                  className="flex-1 bg-green-500 hover:bg-green-600"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Update Task
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
